import React, { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import ListItem from "@mui/material/ListItem";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";

import Close from "mdi-material-ui/Close";
import FileDocumentOutline from "mdi-material-ui/FileDocumentOutline";

import DefaultPalette from "@core/theme/palettes";
import FormHeader from "views/components/formheader";
import FormButtons from "views/components/formbuttons";
import APICall from "network/APICall";
import EndPoints from "network/Endpoints";
import { isSpaceValidateTextField } from "@core/utils/Const";
import Loader from "@core/helper/Loader";

const customTheme = DefaultPalette();

const Img = styled("img")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    marginRight: theme.spacing(5),
    marginLeft: theme.spacing(2),
  },
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    width: 200,
  },
}));

const HeadingTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(2),
  },
}));

const AddBusinessCategory = (props) => {
  const { open, toggle, type, heading, onSubmitCategory, updateData } = props;
  const [files, setFiles] = useState([]);
  const [filename, setFilename] = useState("");

  const theme = useTheme();

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    maxSize: 2000000,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    onDrop: (acceptedFiles) => {
      setFiles(acceptedFiles.map((file) => Object.assign(file)));
      setValue("icon", acceptedFiles);
    },
    onDropRejected: () => {
      toast.error("You can only upload 1 file & maximum size of 2 MB.", {
        duration: 2000,
      });
    },
  });

  const renderFilePreview = (file) => {
    if (file.type.startsWith("image")) {
      return (
        <img
          width={38}
          height={38}
          alt={file.name}
          src={URL.createObjectURL(file)}
        />
      );
    } else {
      return <FileDocumentOutline />;
    }
  };

  const handleRemoveFile = () => {
    setFiles([]);
    setValue("icon", "");
    setFilename("");
  };

  const fileList = files.map((file) => (
    <ListItem
      key={file.name}
      sx={{ justifyContent: "space-between", display: "flex" }}
    >
      <div
        className="file-details"
        style={{ display: "flex", alignItems: "center", width: "90%" }}
      >
        <div className="file-preview">{renderFilePreview(file)}</div>
        <div style={{ marginLeft: 2, flexDirection: "column", width: "85%" }}>
          <Typography
            className="file-name"
            variant="body2"
            sx={{ wordWrap: "break-word" }}
          >
            {file.name}
          </Typography>
          <Typography className="file-size" variant="body2">
            {Math.round(file.size / 100) / 10 > 1000
              ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} mb`
              : `${(Math.round(file.size / 100) / 10).toFixed(1)} kb`}
          </Typography>
        </div>
      </div>
      <IconButton onClick={() => handleRemoveFile()}>
        <Close fontSize="small" />
      </IconButton>
    </ListItem>
  ));

  const defaultValues = {
    category: "",
    icon: "",
  };

  const {
    setValue,
    reset,
    control,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues });

  const resetToEmptyValues = useCallback(() => {
    setValue("category", "");
    setValue("icon", "");
    setFiles([]);
  }, [setValue]);

  const resetToStoredValues = useCallback(() => {
    if (type === "edit") {
      const event = {
        category: updateData.name,
        icon: !filename ? updateData.icon : filename,
      };
      setFilename(event.icon || "");
      setValue("category", event.category || "");
      setValue("icon", event.icon || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, type]);

  useEffect(() => {
    if (type === "edit") {
      resetToStoredValues();
    } else {
      resetToEmptyValues();
    }
  }, [open, resetToStoredValues, resetToEmptyValues, type]);

  const handleClose = () => {
    clearErrors();
    if (type === "add") {
      reset(defaultValues);
    }
    toggle();
  };

  const submitCategory = (data, selectImage) => {
    Loader.isLoading(true);
    if (type === "add") {
      const payload = {
        name: data.category,
        icon: selectImage,
      };

      APICall("post", payload, EndPoints.addBusinessCategory)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitCategory();
            handleClose();
          } else {
            toast.error(message);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          Loader.isLoading(false);
        });
    } else {
      const payload = {
        id: updateData.id,
        name: data.category,
        icon: !selectImage ? "" : selectImage,
      };
      APICall("post", payload, EndPoints.updateBusinessCategory)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitCategory();
            handleClose();
          } else {
            toast.error(message);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          Loader.isLoading(false);
        });
    }
  };

  const onSubmit = (values) => {
    if (files.length === 0) {
      submitCategory(values, "");
      return;
    }
    Loader.isLoading(true);
    const formData = new FormData();
    formData.append("image", values.icon[0]);
    formData.append("type", "business_category");

    APICall("post", formData, EndPoints.uploadImage)
      .then((response) => {
        const { isError, data, message } = response.data;
        if (!isError) {
          submitCategory(values, data.fileName);
        } else {
          toast.error(message);
        }
      })
      .catch((error) => {
        return error;
      })
      .finally(() => {
        Loader.isLoading(false);
      });
  };

  return (
    <Drawer
      open={open}
      anchor="right"
      variant="temporary"
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { width: { xs: 300, sm: 400 } } }}
    >
      <FormHeader heading={heading} onClick={handleClose} />
      <Box sx={{ p: 5 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="category"
                  control={control}
                  rules={{
                    required: true,
                    validate: isSpaceValidateTextField,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Category"
                      error={Boolean(errors.category)}
                      placeholder="Enter category"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.category && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              {files.length === 0 && type === "edit" && filename ? (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderWidth: 1,
                      borderStyle: "dashed",
                      borderColor: customTheme.grey.A200,
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 2,
                      display: "flex",
                      cursor: "pointer",
                    }}
                  >
                    <Img width={100} alt="Upload img" src={updateData.icon} />
                    <IconButton onClick={() => handleRemoveFile()}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ) : files.length > 0 ? (
                <div>
                  <Box
                    sx={{
                      borderWidth: 1,
                      borderRadius: 1,
                      borderStyle: "dashed",
                      borderColor: customTheme.grey.A200,
                    }}
                  >
                    <List>{fileList}</List>
                  </Box>
                </div>
              ) : (
                <FormControl fullWidth>
                  <Controller
                    name="icon"
                    control={control}
                    rules={{ required: true }}
                    render={() => (
                      <div {...getRootProps({ className: "dropzone" })}>
                        <input {...getInputProps()} />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: ["column", "column", "row"],
                            alignItems: "center",
                            borderWidth: 1,
                            borderRadius: 1,
                            borderStyle: "dashed",
                            borderColor: errors.icon
                              ? theme.palette.error.dark
                              : customTheme.grey.A200,
                          }}
                        >
                          <Img
                            width={100}
                            alt="Upload img"
                            src="/images/misc/upload.png"
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              textAlign: ["center", "center", "inherit"],
                            }}
                          >
                            <HeadingTypography>
                              Drop file here or click to upload.
                            </HeadingTypography>
                            <Typography color="textSecondary" variant="body2">
                              Allowed *.jpeg, *.jpg, *.png
                            </Typography>
                            <Typography color="textSecondary" variant="body2">
                              Max 1 file and max size of 2 MB
                            </Typography>
                          </Box>
                        </Box>
                      </div>
                    )}
                  />
                  {errors.icon && (
                    <FormHelperText
                      sx={{ color: "error.main" }}
                      id="validation-basic-first-name"
                    >
                      This field is required
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            </Grid>

            <FormButtons type={type} resetFunction={resetToEmptyValues} />
          </Grid>
        </form>
      </Box>
    </Drawer>
  );
};

export default AddBusinessCategory;
